import { useState, useEffect, useCallback } from 'react';
import { filterOptionsAPI } from '../../services/api';
import { getStoredAuthToken } from '../../utils/authStorage';

const resolveAuthToken = (tokenFromProps) => {
  if (typeof tokenFromProps === 'string' && tokenFromProps.trim()) {
    return tokenFromProps.trim();
  }
  return getStoredAuthToken();
};

function seedStaticOptions(fields) {
  const seeded = {};
  for (const field of fields || []) {
    if (Array.isArray(field.options) && field.options.length > 0) {
      seeded[field.name] = field.options;
    }
  }
  return seeded;
}

export const useFilterOptions = (token) => {
  const [filterFields, setFilterFields] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const authToken = resolveAuthToken(token);

  useEffect(() => {
    const fetchFilterFields = async () => {
      try {
        const response = await filterOptionsAPI.getFields();
        const fields = Array.isArray(response.data?.fields) ? response.data.fields : [];
        setFilterFields(fields);
        setFieldOptions((prev) => ({ ...seedStaticOptions(fields), ...prev }));
      } catch (error) {
        console.error('Error fetching filter fields:', error);
      }
    };

    if (authToken) {
      fetchFilterFields();
    }
  }, [authToken]);

  const fetchFieldOptions = useCallback(
    async (fieldName) => {
      if (fieldOptions[fieldName]?.length || loadingOptions[fieldName]) {
        return;
      }

      const fieldDef = filterFields.find((f) => f.name === fieldName);
      if (fieldDef?.options?.length) {
        setFieldOptions((prev) => ({
          ...prev,
          [fieldName]: fieldDef.options,
        }));
        return;
      }

      setLoadingOptions((prev) => ({ ...prev, [fieldName]: true }));

      try {
        const response = await filterOptionsAPI.getOptionsForField(fieldName);
        setFieldOptions((prev) => ({
          ...prev,
          [fieldName]: response.data.options || [],
        }));
      } catch (error) {
        console.error(`Error fetching options for ${fieldName}:`, error);
        setFieldOptions((prev) => ({
          ...prev,
          [fieldName]: [],
        }));
      } finally {
        setLoadingOptions((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [fieldOptions, filterFields, loadingOptions]
  );

  return {
    filterFields,
    fieldOptions,
    loadingOptions,
    fetchFieldOptions,
  };
};
